import { RotateCw } from 'lucide-react';
import { authErrorMessage } from '../../api/auth-messages.ts';
import { isApiError } from '../../api/errors.ts';
import { cn } from '../../lib/cn.ts';
import { Button } from '../ui/button.tsx';

/**
 * Erro = mensagem da API + "tentar de novo" + o código do erro (`spec/17` §4.3 item 3).
 * O `request_id` sai em `data-value` (mono) e selecionável: é o que o suporte pede.
 *
 * Desenho (WEB-133, fase 2): célula neutra, sem moldura nem fundo vermelho. A marca do
 * estado é a matriz de pontos da marca interrompida, nove quadrados em degraus de tinta
 * com o último na cor de erro. Parada e em CSS: a Visão geral chega a montar quatro
 * erros de uma vez, e o `DitherField` (WebGL) não é pra isso.
 *
 * O tamanho segue o CONTÊINER, não a janela (`@container`): numa área larga (página,
 * cartão de lista) vira uma faixa com a marca à esquerda e a ação à direita; numa área
 * estreita (gaveta, cartão de coluna) empilha e aperta, pra não virar um bloco gigante
 * dentro de um painel pequeno. `compact` força o desenho estreito em qualquer largura.
 */
export function ErrorState({
  error,
  onRetry,
  compact = false,
}: {
  error: unknown;
  onRetry?: () => void;
  compact?: boolean;
}) {
  // Mesma tradução por `code` de `errorMessageOf`; aqui o `request_id` sai em linha
  // própria, então a mensagem não leva o sufixo.
  const message = isApiError(error)
    ? (authErrorMessage(error.code) ?? error.message)
    : error instanceof Error
      ? error.message
      : 'erro inesperado';
  const requestId = isApiError(error) ? error.requestId : '';
  const wide = !compact;

  return (
    // `w-full`: `@container` zera a largura intrínseca, então num pai que mede pelo
    // conteúdo o bloco sumiria sem isto.
    <div role="alert" className="@container w-full min-w-0 text-left">
      <div
        className={cn(
          'grid grid-cols-[auto_minmax(0,1fr)] items-start gap-3 rounded-lg bg-secondary p-4',
          wide &&
            '@xl:grid-cols-[auto_minmax(0,1fr)_auto] @xl:items-center @xl:gap-4 @xl:rounded-2xl @xl:p-5',
        )}
      >
        <FaultMark className={cn(wide && '@xl:size-11')} />
        <div className="grid min-w-0 gap-1">
          <p className={cn('text-14 font-semibold', wide && '@xl:text-15')}>
            Não deu para carregar
          </p>
          <p className={cn('max-w-[60ch] text-13 text-muted-foreground', wide && '@xl:text-14')}>
            {message}
          </p>
          {requestId !== '' && (
            <p className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-12 text-muted-foreground">
              código do erro:
              <span className="data-value rounded-md bg-card px-1.5 py-0.5 text-foreground break-all shadow-(--shadow-card) select-all">
                {requestId}
              </span>
            </p>
          )}
        </div>
        {onRetry !== undefined && (
          <Button
            variant="outline"
            size="sm"
            // Estreito: embaixo do texto, alinhado com ele. Largo: à direita, na mesma linha.
            className={cn(
              'col-start-2 justify-self-start bg-card',
              wide && '@xl:col-start-3 @xl:row-start-1',
            )}
            onClick={onRetry}
          >
            <RotateCw aria-hidden="true" />
            Tentar de novo
          </Button>
        )}
      </div>
    </div>
  );
}

/** Tinta de cada ponto, do canto de cima ao de baixo; o nono é o da falha. */
const RAMP = [
  'opacity-15',
  'opacity-30',
  'opacity-50',
  'opacity-30',
  'opacity-50',
  'opacity-75',
  'opacity-50',
  'opacity-75',
] as const;

function FaultMark({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'grid size-9 shrink-0 place-items-center rounded-lg bg-card shadow-(--shadow-card)',
        className,
      )}
    >
      <span className="grid grid-cols-3 gap-[3px]">
        {RAMP.map((tone, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: nove pontos fixos, sem identidade nem reordenação.
          <span key={index} className={cn('size-1 bg-foreground', tone)} />
        ))}
        <span className="size-1 bg-danger" />
      </span>
    </span>
  );
}
